import { SAFETY_QUESTIONS, type SafetyQuestion } from "./questions";

/**
 * Lab Safety Showdown - pure question selection + scoring logic.
 *
 * The bank in `questions.ts` is hand-authored, so unlike Speed Quiz / Reaction
 * Race the engine never synthesises distractors. Its job is to draw a fresh
 * question that hasn't been asked yet (with a graceful fallback) and resolve
 * answer outcomes.
 *
 * Scoring uses an HP/lives model - same shape as Chem Duel - so wrong answers
 * deduct lives instead of awarding points.
 */

export const STARTING_LIVES = 3;
/** Per-turn timer in seconds. Buzzer race uses a separate, longer clock. */
export const SAFETY_TURN_SECONDS = 15;
/** Buzzer race per-question timer (longer because both players read at once). */
export const BUZZER_TURN_SECONDS = 12;
/** Buzzer race: total questions per match. */
export const BUZZER_QUESTIONS = 10;

export type SafetyOutcome = "correct" | "wrong" | "timeout";

export interface SafetyAnswerResult {
  outcome: SafetyOutcome;
  livesDelta: number; // 0 for correct, -1 for wrong/timeout
}

/** Pick one question from the bank, avoiding `excludeIds` when possible. */
export function generateSafetyQuestion(
  rng: () => number,
  excludeIds: ReadonlySet<string>,
): SafetyQuestion {
  const available = SAFETY_QUESTIONS.filter((q) => !excludeIds.has(q.id));
  const pool = available.length > 0 ? available : SAFETY_QUESTIONS;
  return pool[Math.floor(rng() * pool.length)];
}

/** Resolve an answer attempt. Wrong or timeout deducts a single life. */
export function scoreSafetyAnswer(
  chosenIndex: number | null,
  question: SafetyQuestion,
): SafetyAnswerResult {
  if (chosenIndex === null) return { outcome: "timeout", livesDelta: -1 };
  if (chosenIndex !== question.correctIndex) return { outcome: "wrong", livesDelta: -1 };
  return { outcome: "correct", livesDelta: 0 };
}

/** Buzzer-race scoring: speed bonus on correct, no penalty on wrong. */
export const BUZZER_POINTS_FAST = 15;
export const BUZZER_POINTS_SLOW = 8;
export const BUZZER_FAST_THRESHOLD_S = 4;

export interface BuzzerAnswerResult {
  outcome: "fast" | "slow" | "wrong";
  points: number;
}

export function scoreBuzzerAnswer(
  chosenIndex: number,
  question: SafetyQuestion,
  elapsedMs: number,
): BuzzerAnswerResult {
  if (chosenIndex !== question.correctIndex) {
    return { outcome: "wrong", points: 0 };
  }
  if (elapsedMs <= BUZZER_FAST_THRESHOLD_S * 1000) {
    return { outcome: "fast", points: BUZZER_POINTS_FAST };
  }
  return { outcome: "slow", points: BUZZER_POINTS_SLOW };
}